interface Character {
  id: string;
  name: string;
  role: 'protagonist' | 'antagonist' | 'supporting';
  traits: string[];
  backstory: string;
  arc: { startingState: string; transformation: string; endingState: string; shape: string };
  relationships: Array<{ characterId: string; type: string; valence: string }>;
}

interface World {
  setting: { timePeriod: string; locations: string[]; geography?: string };
  rules: Array<{ name: string; description: string }>;
  lore: { history?: string; mythology?: string; culturalNorms: string[] };
  systems: Array<{ name: string; type: string; description: string }>;
}

interface Narrative {
  premise: string;
  theme: string;
  centralConflict: string;
  subplots: Array<{ name: string; conflict: string; resolution?: string; characters: string[] }>;
  tone: { mood: string; narratorVoice: string; proseStyle: string[] };
}

interface StoryBible {
  characters: Character[];
  world: World;
  narrative: Narrative;
}

interface BiblePreviewProps {
  bible: StoryBible;
}

const ROLE_BADGES: Record<string, string> = {
  protagonist: 'text-primary',
  antagonist: 'text-destructive',
  supporting: 'text-muted-foreground',
};

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <summary className="cursor-pointer text-sm font-medium text-foreground py-2 select-none list-none [&::-webkit-details-marker]:hidden flex items-center gap-2 group rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1">
      <span className="text-muted-foreground transition-transform duration-200 motion-reduce:transition-none group-open:rotate-90 text-xs" aria-hidden="true">
        &#9654;
      </span>
      {children}
    </summary>
  );
}

function CharacterEntry({ character, allCharacters }: { character: Character; allCharacters: Character[] }) {
  return (
    <div className="pl-5 py-2 border-b border-border/50 last:border-b-0">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-medium text-foreground">{character.name}</span>
        <span className={`text-[10px] uppercase tracking-wider ${ROLE_BADGES[character.role] ?? 'text-muted-foreground'}`}>
          {character.role}
        </span>
      </div>

      {character.traits.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {character.traits.join(', ')}
        </p>
      )}

      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Arc</span>
          <p className="text-xs text-muted-foreground font-display italic [font-variation-settings:'opsz'_48,'SOFT'_80]">
            {character.arc.shape}
          </p>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Starting</span>
          <p className="text-xs text-muted-foreground font-display italic [font-variation-settings:'opsz'_48,'SOFT'_80]">
            {character.arc.startingState}
          </p>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Ending</span>
          <p className="text-xs text-muted-foreground font-display italic [font-variation-settings:'opsz'_48,'SOFT'_80]">
            {character.arc.endingState}
          </p>
        </div>
      </div>

      {character.relationships.length > 0 && (
        <div className="mt-2">
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Relationships</span>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {character.relationships.map((rel, i) => {
              const target = allCharacters.find((c) => c.id === rel.characterId);
              return (
                <span key={i} className="text-xs text-muted-foreground">
                  {target?.name ?? rel.characterId}: {rel.type}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function WorldSection({ world }: { world: World }) {
  return (
    <div className="pl-5 space-y-3 py-2">
      <div>
        <span className="text-[10px] uppercase tracking-wider text-placeholder">Setting</span>
        <p className="text-sm text-foreground">{world.setting.timePeriod}</p>
        {world.setting.locations.length > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {world.setting.locations.join(', ')}
          </p>
        )}
        {world.setting.geography && (
          <p className="text-xs text-muted-foreground font-display italic [font-variation-settings:'opsz'_48,'SOFT'_80] mt-1">
            {world.setting.geography}
          </p>
        )}
      </div>

      {world.rules.length > 0 && (
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Rules</span>
          {world.rules.map((rule, i) => (
            <div key={i} className="mt-1">
              <span className="text-xs font-medium text-foreground">{rule.name}</span>
              <p className="text-xs text-muted-foreground">{rule.description}</p>
            </div>
          ))}
        </div>
      )}

      {(world.lore.history || world.lore.culturalNorms.length > 0) && (
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Lore</span>
          {world.lore.history && (
            <p className="text-xs text-muted-foreground font-display italic [font-variation-settings:'opsz'_48,'SOFT'_80] mt-1">
              {world.lore.history}
            </p>
          )}
          {world.lore.culturalNorms.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {world.lore.culturalNorms.map((norm, i) => (
                <li key={i} className="text-xs text-muted-foreground">{norm}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {world.systems.length > 0 && (
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Systems</span>
          {world.systems.map((sys, i) => (
            <div key={i} className="mt-1">
              <span className="text-xs font-medium text-foreground">{sys.name}</span>
              <span className="text-[10px] text-placeholder ml-2">{sys.type}</span>
              <p className="text-xs text-muted-foreground">{sys.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NarrativeSection({ narrative }: { narrative: Narrative }) {
  return (
    <div className="pl-5 space-y-3 py-2">
      <div>
        <span className="text-[10px] uppercase tracking-wider text-placeholder">Premise</span>
        <p className="text-sm text-foreground font-display italic [font-variation-settings:'opsz'_48,'SOFT'_80]">
          {narrative.premise}
        </p>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-wider text-placeholder">Theme</span>
        <p className="text-xs text-muted-foreground">{narrative.theme}</p>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-wider text-placeholder">Central Conflict</span>
        <p className="text-xs text-muted-foreground">{narrative.centralConflict}</p>
      </div>

      {narrative.subplots.length > 0 && (
        <div>
          <span className="text-[10px] uppercase tracking-wider text-placeholder">Subplots</span>
          {narrative.subplots.map((subplot, i) => (
            <div key={i} className="mt-1">
              <span className="text-xs font-medium text-foreground">{subplot.name}</span>
              <p className="text-xs text-muted-foreground">{subplot.conflict}</p>
            </div>
          ))}
        </div>
      )}

      <div>
        <span className="text-[10px] uppercase tracking-wider text-placeholder">Tone</span>
        <p className="text-xs text-muted-foreground">
          {narrative.tone.mood}
          <span className="mx-1.5" aria-hidden="true">·</span>
          {narrative.tone.narratorVoice}
        </p>
        {narrative.tone.proseStyle.length > 0 && (
          <p className="text-xs text-placeholder mt-0.5">
            {narrative.tone.proseStyle.join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}

export function BiblePreview({ bible }: BiblePreviewProps) {
  if (!bible) return null;

  return (
    <section>
      <div className="text-[10px] tracking-[0.14em] uppercase text-placeholder mb-4">
        <span className="text-primary/60" aria-hidden="true">§</span> Story Bible
      </div>

      <div className="space-y-1">
        <details>
          <SectionHeader>
            Characters
            <span className="text-xs text-placeholder font-normal">({bible.characters.length})</span>
          </SectionHeader>
          <div>
            {bible.characters.map((char) => (
              <CharacterEntry
                key={char.id}
                character={char}
                allCharacters={bible.characters}
              />
            ))}
          </div>
        </details>

        <details>
          <SectionHeader>World</SectionHeader>
          <WorldSection world={bible.world} />
        </details>

        <details open>
          <SectionHeader>Narrative</SectionHeader>
          <NarrativeSection narrative={bible.narrative} />
        </details>
      </div>
    </section>
  );
}
